import { useEffect, useState, useCallback, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  MegaphoneIcon, Send, SkipForward, CheckCircle2, RotateCcw, X, RefreshCw, MessageSquare,
} from "lucide-react";
import api from "@/lib/api";
import { formatBill } from "@/lib/format";
import { toast } from "sonner";
import { buildCollectionReadyMessage } from "@/components/WhatsAppMessageModal";

/**
 * Bulk WhatsApp Ready-for-Collection broadcast.
 *
 * UX: staff picks a shared editable body, ticks/unticks the customers who
 * should be notified, then clicks "Send Next" repeatedly — each click opens
 * ONE wa.me tab (kept inside the user gesture so browser popup blockers
 * don't fire). Sent rows are marked green and skipped on future clicks.
 *
 * We only prepend "Hello {customer_name}," to the shared body at send-time.
 */
export default function ReadyBroadcastDialog({ open, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // Shared body (no greeting) — greeting is prepended per row at send-time.
  const DEFAULT_BODY = "Your document is ready for collection. Please visit our office between 10:00 AM to 7:00 PM at your convenience to collect it.";
  const [body, setBody] = useState(DEFAULT_BODY);
  const [selected, setSelected] = useState({}); // { [id]: true }
  const [sentIds, setSentIds] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/ready-for-broadcast");
      const list = data?.items || [];
      setItems(list);
      // preselect everyone by default
      const s = {};
      list.forEach((r) => { s[r.id] = true; });
      setSelected(s);
      setSentIds(new Set());
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Ordered queue of unsent + selected rows
  const queue = useMemo(
    () => items.filter((r) => selected[r.id] && !sentIds.has(r.id)),
    [items, selected, sentIds]
  );
  const nextRow = queue[0] || null;
  const sentCount = sentIds.size;
  const selectedCount = items.filter((r) => selected[r.id]).length;

  const toggleAll = (on) => {
    const s = {};
    items.forEach((r) => { s[r.id] = on; });
    setSelected(s);
  };

  const messageFor = (row) => {
    const name = (row.customer_name || "").trim();
    const greet = name ? `Hello ${name},` : "Hello,";
    return `${greet}\n${body}`;
  };

  const sendOne = (row) => {
    const raw = String(row.mobile || "").replace(/\D/g, "");
    if (!raw) { toast.error(`No mobile for ${row.customer_name}`); return; }
    const url = `https://wa.me/${raw}?text=${encodeURIComponent(messageFor(row))}`;
    // Open in a new tab — must be inside the user-gesture click handler
    window.open(url, "_blank", "noopener,noreferrer");
    setSentIds((prev) => {
      const n = new Set(prev);
      n.add(row.id);
      return n;
    });
  };

  const skipOne = (row) => {
    setSentIds((prev) => {
      const n = new Set(prev);
      n.add(row.id);
      return n;
    });
  };

  const sendNext = () => {
    if (!nextRow) { toast.info("Nothing left to send"); return; }
    sendOne(nextRow);
  };

  const resetProgress = () => {
    setSentIds(new Set());
  };

  const allDone = selectedCount > 0 && sentCount >= selectedCount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0"
        data-testid="ready-broadcast-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-emerald-200 bg-gradient-to-r from-emerald-50 to-white">
          <DialogTitle className="flex items-center gap-2 text-emerald-900">
            <MegaphoneIcon className="h-5 w-5 text-emerald-600" />
            WhatsApp Ready — End-of-Day Broadcast
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {loading
              ? "Loading customers whose work is Completed…"
              : items.length === 0
                ? "No customers are pending collection right now."
                : `${items.length} customer${items.length > 1 ? "s" : ""} completed and awaiting collection. Personalised greeting is prepended automatically.`}
          </DialogDescription>
        </DialogHeader>

        {/* Body editor */}
        <div className="px-6 py-3 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center justify-between mb-1">
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              Message body (personal greeting prepended per customer)
            </Label>
            <button
              type="button"
              onClick={() => setBody(DEFAULT_BODY)}
              className="text-[10px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
              data-testid="broadcast-reset-body"
            >
              <RotateCcw className="h-3 w-3" /> Reset
            </button>
          </div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
            data-testid="broadcast-body-textarea"
          />
          <div className="text-[10px] text-slate-400 mt-1">
            Preview: <span className="italic">"Hello {"{name}"}, {body.slice(0, 90)}{body.length > 90 ? "…" : ""}"</span>
          </div>
        </div>

        {/* Send controls */}
        <div className="px-6 py-3 border-b border-slate-200 bg-white flex items-center gap-3 flex-wrap">
          <Button
            onClick={sendNext}
            disabled={!nextRow}
            className="h-9 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-semibold"
            data-testid="broadcast-send-next"
          >
            <Send className="h-4 w-4 mr-1.5" />
            {nextRow ? `Send Next → ${nextRow.customer_name}` : "All Sent"}
          </Button>
          {nextRow && (
            <Button
              onClick={() => skipOne(nextRow)}
              variant="outline"
              className="h-9 font-semibold"
              data-testid="broadcast-skip-next"
              title="Skip this customer for now"
            >
              <SkipForward className="h-4 w-4 mr-1.5" /> Skip
            </Button>
          )}
          <div className="flex-1" />
          <div className="text-xs text-slate-600 font-semibold" data-testid="broadcast-progress">
            <span className="font-mono-num text-emerald-700">{sentCount}</span>
            <span className="text-slate-400"> / </span>
            <span className="font-mono-num">{selectedCount}</span> sent
          </div>
          <Button
            onClick={resetProgress}
            variant="outline"
            className="h-9 text-xs"
            data-testid="broadcast-reset-progress"
            title="Reset sent progress"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Reset Progress
          </Button>
        </div>

        {/* Customer list */}
        <div className="flex-1 overflow-auto px-6 py-3">
          {items.length === 0 && !loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mb-3" />
              <div className="text-sm font-semibold text-slate-600">All caught up</div>
              <div className="text-xs">No completed services awaiting collection.</div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-2 text-[11px] text-slate-500">
                <button
                  onClick={() => toggleAll(true)}
                  className="hover:underline"
                  data-testid="broadcast-select-all"
                >Select All</button>
                <span>·</span>
                <button
                  onClick={() => toggleAll(false)}
                  className="hover:underline"
                  data-testid="broadcast-deselect-all"
                >Clear All</button>
              </div>
              <table className="w-full text-sm crm-table" data-testid="broadcast-table">
                <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider sticky top-0">
                  <tr>
                    <th className="px-3 py-2 w-8"></th>
                    <th className="px-3 py-2 text-left font-semibold">Customer</th>
                    <th className="px-3 py-2 text-left font-semibold">Mobile</th>
                    <th className="px-3 py-2 text-left font-semibold">Doc No</th>
                    <th className="px-3 py-2 text-left font-semibold">Service</th>
                    <th className="px-3 py-2 text-left font-semibold">Completed</th>
                    <th className="px-3 py-2 text-right font-semibold w-40">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((r) => {
                    const isSent = sentIds.has(r.id);
                    return (
                      <tr
                        key={r.id}
                        className={isSent ? "bg-emerald-50" : ""}
                        data-testid={`broadcast-row-${r.id}`}
                      >
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={!!selected[r.id]}
                            onChange={(e) => setSelected((s) => ({ ...s, [r.id]: e.target.checked }))}
                            disabled={isSent}
                            className="h-4 w-4 accent-emerald-600"
                            data-testid={`broadcast-check-${r.id}`}
                          />
                        </td>
                        <td className="px-3 py-2 font-semibold text-slate-800">
                          {r.customer_name || "—"}
                          {isSent && (
                            <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-bold text-emerald-700 uppercase">
                              <CheckCircle2 className="h-3 w-3" /> Sent
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono-num text-slate-700">{r.mobile}</td>
                        <td className="px-3 py-2 font-mono-num text-slate-600">{formatBill(r.doc_no)}</td>
                        <td className="px-3 py-2 text-slate-600 text-xs">{r.service_name}</td>
                        <td className="px-3 py-2 text-xs text-slate-500 font-mono-num">{r.completed_date || "—"}</td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            size="sm"
                            onClick={() => sendOne(r)}
                            disabled={isSent}
                            className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-[11px] font-semibold"
                            data-testid={`broadcast-send-${r.id}`}
                          >
                            <MessageSquare className="h-3 w-3 mr-1" /> Send
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-[11px] text-slate-500">
            Each click opens ONE WhatsApp tab so your browser's popup blocker never interferes.
            {allDone && <span className="ml-1 text-emerald-700 font-semibold">All selected customers notified.</span>}
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={load}
              disabled={loading}
              variant="outline"
              className="h-8 text-xs font-semibold"
              data-testid="broadcast-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button
              onClick={() => onOpenChange(false)}
              className="h-8 bg-[#2E77E6] hover:bg-[#2361C4] text-white text-xs font-semibold"
              data-testid="broadcast-close"
            >
              <X className="h-3.5 w-3.5 mr-1.5" /> Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
